import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

// Shared, reusable chart toolkit for the table-style reports. Each report computes
// a small [{name, value}] dataset (via groupCount or an inline reducer) and drops a
// <BarCard>/<DonutCard> into a <ReportChartRow>. Charts are display-only — they read
// the already-loaded report rows, so they add no extra API calls and stay in sync
// with the active filters/date range.

export const CHART_PALETTE = [
  '#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#64748B',
];

const TIP = {
  contentStyle: { borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 6px 16px rgba(15,23,42,0.10)' },
  labelStyle: { fontWeight: 600, color: '#334155' },
  cursor: { fill: 'rgba(79,70,229,0.06)' },
};

// Count rows by a categorical field → [{name, value}] sorted desc, capped to `limit`.
// `label` lets callers prettify the bucket name (e.g. capitalise a status).
export const groupCount = (data, key, { limit = 10, label } = {}) => {
  const m = new Map();
  (data || []).forEach((r) => {
    let k = r[key];
    k = (k === null || k === undefined || k === '') ? '—'
      : label ? label(k, r) : String(k);
    m.set(k, (m.get(k) || 0) + 1);
  });
  return [...m.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

// Expiry status buckets from a date field → Expired / ≤30 days / Valid.
export const EXPIRY_COLORS = ['#EF4444', '#F59E0B', '#10B981'];
export const expiryBuckets = (data, key = 'expiry_date') => {
  const now = Date.now();
  let expired = 0, soon = 0, ok = 0;
  (data || []).forEach((r) => {
    const t = r[key] ? new Date(r[key]).getTime() : null;
    if (t == null || isNaN(t)) return;
    if (t < now) expired++;
    else if (t < now + 30 * 86400000) soon++;
    else ok++;
  });
  return [{ name: 'Expired', value: expired }, { name: '≤30 days', value: soon }, { name: 'Valid', value: ok }];
};

// Punctuality buckets from a clock-variance-in-minutes field → Early / On time / Late.
export const VARIANCE_COLORS = ['#06B6D4', '#10B981', '#EF4444'];
export const varianceBuckets = (data, key, tol = 5) => {
  let early = 0, ontime = 0, late = 0;
  (data || []).forEach((r) => {
    const v = r[key];
    if (v == null || v === '') return;
    const n = Number(v);
    if (isNaN(n)) return;
    if (n <= -tol) early++;
    else if (n >= tol) late++;
    else ontime++;
  });
  return [{ name: `Early (≥${tol}m)`, value: early }, { name: 'On time', value: ontime }, { name: `Late (≥${tol}m)`, value: late }];
};

// Map already-aggregated rows → [{name, value}] using a name column and a value
// column (for backend reports that already grouped, e.g. service-type counts).
export const asSeries = (data, nameKey, valueKey, { limit = 12, label } = {}) =>
  (data || [])
    .map((r) => ({
      name: label ? label(r[nameKey], r) : (r[nameKey] == null || r[nameKey] === '' ? '—' : String(r[nameKey])),
      value: Number(r[valueKey]) || 0,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

// Sum a numeric column grouped by a categorical field → [{name, value}] desc, capped.
export const groupSum = (data, key, sumKey, { limit = 12, label } = {}) => {
  const m = new Map();
  (data || []).forEach((r) => {
    let k = r[key];
    k = (k == null || k === '') ? '—' : label ? label(k, r) : String(k);
    m.set(k, (m.get(k) || 0) + (Number(r[sumKey]) || 0));
  });
  return [...m.entries()]
    .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

// Total of a numeric column across all rows.
export const sumField = (data, key) =>
  (data || []).reduce((s, r) => s + (Number(r[key]) || 0), 0);

// Bucket rows by calendar month from a date field → [{name, value}] in chronological
// order (last `limit` months). Parses 'YYYY-MM…' strings directly (no TZ day-shift).
export const groupByMonth = (data, key, { limit = 12 } = {}) => {
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = new Map();
  (data || []).forEach((r) => {
    const s = r[key] == null ? '' : String(r[key]);
    const mm = /^(\d{4})-(\d{2})/.exec(s);
    let y, mo;
    if (mm) { y = +mm[1]; mo = +mm[2] - 1; }
    else { const d = new Date(s); if (isNaN(d)) return; y = d.getFullYear(); mo = d.getMonth(); }
    if (mo < 0 || mo > 11) return;
    const k = `${y}-${String(mo + 1).padStart(2, '0')}`;
    const cur = m.get(k) || { name: `${MON[mo]} ${String(y).slice(2)}`, value: 0 };
    cur.value++;
    m.set(k, cur);
  });
  return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([, v]) => v).slice(-limit);
};

const Empty = () => (
  <div className="h-full flex items-center justify-center text-xs text-slate-300">No data to chart</div>
);

const Card = ({ title, subtitle, children, height = 230 }) => (
  <div className="flex-1 min-w-[300px] bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
    <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    {subtitle && <p className="text-[11px] text-slate-400 mb-1">{subtitle}</p>}
    <div style={{ width: '100%', height }}>{children}</div>
  </div>
);

// A flex row of chart cards. Drops falsy/empty children so a report can include a
// chart conditionally without leaving a gap.
export const ReportChartRow = ({ children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return <div className="flex flex-wrap gap-4">{items}</div>;
};

export const BarCard = ({ title, subtitle, data, color = '#4F46E5', horizontal = false, height }) => (
  <Card title={title} subtitle={subtitle} height={height}>
    {!data?.length ? <Empty /> : (
      <ResponsiveContainer>
        <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 14, left: horizontal ? 4 : -14, bottom: horizontal ? 4 : 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={!horizontal} vertical={horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis type="category" dataKey="name" width={124} tick={{ fontSize: 11, fill: '#475569' }} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" interval={0} height={50} tick={{ fontSize: 10, fill: '#475569' }} angle={-18} textAnchor="end" />
              <YAxis allowDecimals={false} width={34} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            </>
          )}
          <Tooltip {...TIP} />
          <Bar dataKey="value" fill={color} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]} maxBarSize={46} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </Card>
);

export const DonutCard = ({ title, subtitle, data, colors = CHART_PALETTE, height }) => (
  <Card title={title} subtitle={subtitle} height={height}>
    {!data?.length || data.every((d) => !d.value) ? <Empty /> : (
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={84} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip {...TIP} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Card>
);
